'use client';

import { c as _c } from "react/compiler-runtime";
import { useSyncExternalStore } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import type { TemplateFirebaseTypeWithId } from '@poppy/firebase';
import { isPartnerSlug, partnerSlugFromHost, type Partner } from '@/lib/whiteLabel/partners';
const subscribe = () => () => {};
const NO_TEMPLATES: TemplateFirebaseTypeWithId[] = [];
const PREVIEW_KEY = 'poppy-partner-preview';
function readPartnerSlug(): string | null {
  const fromHost = partnerSlugFromHost(window.location.hostname);
  if (fromHost) return fromHost;
  // Local preview: localhost has no partner subdomain, so allow ?partner=<slug>. It sticks
  // for the tab, since board links drop the query; ?partner= with no value clears it.
  if (process.env.NODE_ENV !== 'production') {
    const params = new URLSearchParams(window.location.search);
    if (params.has('partner')) {
      const fromQuery = params.get('partner') ?? '';
      if (isPartnerSlug(fromQuery)) sessionStorage.setItem(PREVIEW_KEY, fromQuery);else sessionStorage.removeItem(PREVIEW_KEY);
    }
    const preview = sessionStorage.getItem(PREVIEW_KEY);
    if (preview && isPartnerSlug(preview)) return preview;
  }
  return null;
}

/** The partner slug for the current domain, or null on regular Poppy. */
export function usePartnerSlug() {
  return useSyncExternalStore(subscribe, readPartnerSlug, _temp);
}
function _temp() {
  return null;
}
export const partnerQueryKey = (slug: string) => ['partner', slug] as const;

/**
 * The white-label partner for the current domain. Regular Poppy never fetches;
 * an unknown or draft partner resolves to null, so pages fall back to Poppy branding.
 */
export function usePartnerQuery() {
  const $ = _c(8);
  const slug = usePartnerSlug();
  const t0 = slug ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = partnerQueryKey(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = slug !== null;
  let t3;
  if ($[2] !== slug) {
    t3 = async () => {
      ;
      try {
        return (await axios.get(`/api/partners/${slug}`)).data;
      } catch (t4) {
        const error = t4;
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    };
    $[2] = slug;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      enabled: t2,
      staleTime: 300000,
      retry: false,
      queryFn: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
}
export function usePartner() {
  return usePartnerQuery().data ?? null;
}

/**
 * On a partner's domain: whether to brand the page (true while the partner is still
 * loading, so Poppy's own content never flashes first) and the partner's templates.
 */
export function usePartnerTemplates() {
  const $ = _c(13);
  const slug = usePartnerSlug();
  const {
    data: partner,
    isLoading: partnerLoading
  } = usePartnerQuery();
  const isPartnerDomain = slug !== null && (partnerLoading || partner != null);
  const t0 = slug ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = ["partner-templates", t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = slug !== null && partner != null;
  let t3;
  if ($[2] !== slug) {
    t3 = async () => (await axios.get(`/api/partners/${slug}/templates`)).data.templates;
    $[2] = slug;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      enabled: t2,
      staleTime: 300000,
      queryFn: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const query = useQuery(t4);
  const t5 = partner ?? null;
  const t6 = query.data ?? NO_TEMPLATES;
  const t7 = isPartnerDomain && (partnerLoading || query.isLoading);
  let t8;
  if ($[8] !== isPartnerDomain || $[9] !== t5 || $[10] !== t6 || $[11] !== t7) {
    t8 = {
      isPartnerDomain,
      partner: t5,
      templates: t6,
      loading: t7
    };
    $[8] = isPartnerDomain;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}